import { useState } from "react";
import {
  openBillingPortal,
  type BillingManage,
  type BillingPortalFlow,
  invoiceLink,
} from "../../lib/api";
import { savePendingPortal, takePendingPortal } from "../../lib/draft";
import { statedDate } from "./plan";
import { NavRow } from "../shell/surfaces";

/** The rows under Manage, in order (wave 87). "Change plan" is the panel's own. */
export const PAYMENT_METHOD = "Payment method";
export const BILLING_DETAILS = "Billing details";
export const INVOICES_AND_RECEIPTS = "Invoices and receipts";
/** The payment notice's two actions. */
export const UPDATE_CARD = "Update card";
export const PAY_NOW = "Pay now";
/** Where the bank asked for confirmation, not payment. */
export const CONFIRM_PAYMENT = "Confirm payment";
/** A portal the server would not open (503: its own configuration is missing). */
export const PORTAL_UNAVAILABLE = "Billing settings are unavailable right now. Try again later.";
export const PORTAL_FAILED = "Couldn't open billing settings. Try again.";
/** What a screen reader calls the notice. */
export const PAYMENT_NOTICE_LABEL = "A payment needs you";

const BRANDS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  cartes_bancaires: "Cartes Bancaires",
  eftpos_au: "eftpos",
  link: "Link",
};

/** "Visa", "Mastercard" — the processor's brand word, in a person's. */
export function brandName(brand: string): string {
  return BRANDS[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1).replace(/_/g, " ");
}

/** "08/27" — an expiry as the card itself prints it. */
function expiryText(month: number, year: number): string {
  return `${String(month).padStart(2, "0")}/${String(year % 100).padStart(2, "0")}`;
}

/**
 * "Visa ···· 4242 · expires 08/27". An expired card says so, and so does one
 * that expires within 30 days — the server's reading, on its own clock.
 */
export function cardValue(card: NonNullable<BillingManage["card"]>): string {
  const head = `${brandName(card.brand)} ···· ${card.last4}`;
  const when = expiryText(card.exp_month, card.exp_year);
  // The expiry is one phrase: no-break spaces keep "expires 08/27" together,
  // so a narrow row breaks before it and never inside it.
  const nb = (text: string) => text.replace(/ /g, "\u00a0");
  if (card.expiry === "expired") return `${head} · ${nb(`expired ${when}`)}`;
  if (card.expiry === "soon") return `${head} · ${nb(`expires soon, ${when}`)}`;
  return `${head} · ${nb(`expires ${when}`)}`;
}

/** "United Kingdom" for GB — the country's name where the browser knows it. */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * THE PROCESSOR'S BILLING PORTAL, OPENED ON ONE PAGE (wave 87). The round
 * trip is recorded first (`lib/draft.ts`), so the screen that comes back asks
 * the server to read the card fresh; a portal that would not open takes the
 * record back and says so on the row's group.
 */
export function useBillingPortal() {
  const [problem, setProblem] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  async function open(flow: BillingPortalFlow) {
    if (opening) return;
    setOpening(true);
    setProblem(null);
    savePendingPortal(flow);
    const r = await openBillingPortal(flow);
    if (r.ok) {
      window.location.assign(r.url);
      return;
    }
    takePendingPortal();
    setOpening(false);
    setProblem(r.code === "BILLING_PORTAL_UNAVAILABLE" ? PORTAL_UNAVAILABLE : PORTAL_FAILED);
  }
  return { open, problem };
}

/**
 * THE ROWS AFTER "CHANGE PLAN", AND THE NOTICE ABOVE THEM (wave 87).
 *
 * `Payment method` and `Billing details` show only where there is a processor
 * customer — a trial or a paid plan, now or before; `Invoices and receipts`
 * only where at least one invoice is listed. On Free with no history, Manage is
 * "Change plan" alone, as it was.
 *
 * NEITHER ROW IS A CANCEL PATH. They open the processor's portal on the card
 * or on the customer's details, under a configuration the server locks to
 * those two things (docs/plans.md §9 #4); cancelling stays the one POST on the
 * ladder, and changing plan stays our consented disclosure.
 */
export function ManageRows({
  billing,
  onInvoices,
}: {
  billing: BillingManage | null | undefined;
  onInvoices: () => void;
}) {
  const portal = useBillingPortal();
  if (!billing) return null;
  const card = billing.card;
  return (
    <>
      {billing.customer && (
        <NavRow
          label={PAYMENT_METHOD}
          {...(card ? { value: cardValue(card) } : {})}
          {...(card && card.expiry !== "ok" ? { valueTone: "warn" as const } : {})}
          valueWrap
          onClick={() => void portal.open("payment_method")}
        />
      )}
      {billing.customer && (
        <NavRow
          label={BILLING_DETAILS}
          {...(billing.country ? { value: countryName(billing.country) } : {})}
          onClick={() => void portal.open("details")}
        />
      )}
      {billing.latest_invoice_at && (
        <NavRow
          label={INVOICES_AND_RECEIPTS}
          value={statedDate(billing.latest_invoice_at)}
          onClick={onInvoices}
        />
      )}
      {portal.problem && (
        <p role="alert" className="px-3 py-2 text-[12px] leading-snug text-danger">
          {portal.problem}
        </p>
      )}
    </>
  );
}

/**
 * "Pay now" is the notice's one primary action, so it is the amber one — an
 * anchor (the invoice's page opens in a new tab) drawn as the primary button.
 */
const PAY_NOW_CLASS =
  "inline-flex h-9 items-center justify-center rounded-lg bg-accent px-3 text-xs font-medium " +
  "text-accent-fg transition-colors duration-75 hover:bg-accent-soft active:bg-accent-strong " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

/** "Update card", the secondary beside it, at the same height. */
const UPDATE_CARD_CLASS =
  "inline-flex h-9 items-center justify-center rounded-lg border border-border-strong px-3 " +
  "text-xs font-medium text-text transition-colors duration-75 hover:border-border-loud " +
  "hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

/**
 * A PAYMENT THAT NEEDS THE SUBSCRIBER, AT THE TOP OF MANAGE (wave 87): the
 * plan is past due, or an invoice is open after a failed attempt or waiting on
 * the bank's confirmation. One line — the server's — and two ways out: a new
 * card (the portal's card page), or that invoice's own page, where a card can
 * be authenticated or replaced. It is gone once the invoice is paid.
 */
export function PaymentNotice({ billing }: { billing: BillingManage | null | undefined }) {
  const portal = useBillingPortal();
  const notice = billing?.notice;
  if (!notice) return null;
  return (
    <section
      aria-label={PAYMENT_NOTICE_LABEL}
      data-payment-notice={notice.kind}
      className="border-b border-border-soft px-3 py-3"
    >
      <p className="text-[13px] font-medium leading-snug text-warn">{notice.line}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {notice.invoice_id && (
          <a
            href={invoiceLink(notice.invoice_id, "page")}
            target="_blank"
            rel="noreferrer"
            className={PAY_NOW_CLASS}
          >
            {notice.kind === "action_required" ? CONFIRM_PAYMENT : PAY_NOW}
          </a>
        )}
        <button
          type="button"
          onClick={() => void portal.open("payment_method")}
          className={UPDATE_CARD_CLASS}
        >
          {UPDATE_CARD}
        </button>
      </div>
      {portal.problem && (
        <p role="alert" className="mt-2 text-[12px] leading-snug text-danger">
          {portal.problem}
        </p>
      )}
    </section>
  );
}
